import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  MdExpandMore,
  MdExpandLess,
  MdAdd,
  MdChat,
  MdBuild,
  MdLogout,
  MdPerson,
  MdSettings,
  MdSupervisorAccount,
  MdFolder,
  MdSearch,
  MdErrorOutline,
  MdTravelExplore,
  MdOutlineTerrain
} from 'react-icons/md';
import {
  IoChevronBack,
  IoChevronForward,
  IoCloseOutline,
  IoDocumentOutline,
  IoEllipsisHorizontal,
  IoFolderOutline,
  IoPencilOutline,
  IoTrashOutline,
} from 'react-icons/io5';
// Bootstrap's angled pin is the classic tilted thumbtack. Io5's IoPin is a
// map-marker teardrop, which reads as "location" rather than "pinned".
import { BsPinAngle, BsPinAngleFill } from 'react-icons/bs';
import { searchConversations } from './atlas/atlasApi';
import DeleteChatDialog, { NOT_STARTER_MESSAGE } from './atlas/DeleteChatDialog';
import AdminClientSelector from './AdminClientSelector';
import './UnifiedSidebar.css';

function renderSnippet(snippet) {
  if (!snippet) return null;
  const parts = snippet.split(/<<|>>/);
  return parts.map((part, i) =>
    i % 2 === 1
      ? <mark key={i} className="unified-search-highlight">{part}</mark>
      : <span key={i}>{part}</span>
  );
}

/**
 * Whether the chat menu offers "Delete chat". Hidden in the admin view (an
 * admin looking at a client's chats). Disabled when the list already says
 * someone else started the chat; otherwise the dialog's preview has the
 * final say (can_delete / not_starter), since the server decides.
 */
export function chatDeleteAccess(conversation, { user, hasClientContext } = {}) {
  if (hasClientContext) return { visible: false, disabled: true };
  if (conversation?.can_delete === false) return { visible: true, disabled: true };
  const starterId = conversation?.started_by_user_id ?? conversation?.owner_user_id;
  if (starterId != null && user?.id != null && String(starterId) !== String(user.id)) {
    return { visible: true, disabled: true };
  }
  return { visible: true, disabled: false };
}

function buildCompanyInitials(name) {
  const cleaned = String(name || 'Company')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[^\w\s&]/g, ' ')
    .trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (!words.length) return 'C';
  const initials = words
    .filter((word) => !['and', 'the', 'of', 'llc', 'inc', 'co', 'company'].includes(word.toLowerCase()))
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('');
  return initials || words[0].slice(0, 2).toUpperCase();
}

const UnifiedSidebar = ({
  // Chat history props
  conversations,
  conversationsStatus,
  conversationsError,
  onUpdateConversation,
  onConversationDeleted,
  hasMoreConversations,
  loadingMore,
  onLoadMore,
  conversationId,
  loadConversation,
  startNewConversation,
  formatRelativeTime,
  // Admin props
  hasClientContext,
  clients,
  selectedClient,
  onClientChange,
  onRefreshClients,
  searchOwnerUserId,
  // Tools props
  user,
  selectedTool,
  onToolChange,
  // Collapse state
  collapsed,
  onCollapse,
  // Mobile state
  isMobile,
  mobileOpen,
  onMobileClose,
  // User menu
  onLogout,
  onOpenFileBrowser,
  onOpenReleaseNotes,
  onOpenUserSettings,
  atlasUserPreferences,
  onUpdateAtlasUserPreferences,
  workspaceContext,
  workspaceScope,
  onWorkspaceScopeChange,
  onWorkspaceCompanyChange,
  hasCompanyWorkspace,
  workspaceCompanyOptions = [],
  selectedWorkspaceCompanyId
}) => {
  const [toolsExpanded, setToolsExpanded] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [companyScopeConfirmOpen, setCompanyScopeConfirmOpen] = useState(false);
  const [companyScopeDefaultChecked, setCompanyScopeDefaultChecked] = useState(false);
  const [companyScopeDefaultSaving, setCompanyScopeDefaultSaving] = useState(false);
  const userMenuRef = useRef(null);

  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Per-chat kebab menu + inline rename
  const [openChatMenuId, setOpenChatMenuId] = useState(null);
  const [chatMenuPosition, setChatMenuPosition] = useState(null);
  const [renamingChatId, setRenamingChatId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [savingChatId, setSavingChatId] = useState(null);
  const chatMenuRef = useRef(null);
  const renameInputRef = useRef(null);
  // The chat whose "Delete chat" confirmation is open.
  const [deleteTarget, setDeleteTarget] = useState(null);

  // The next page of chats loads when the list is scrolled near its bottom. On a
  // tall window or when zoomed out, the first page can fit without a scrollbar,
  // so nothing could ever scroll: keep loading until the list overflows or runs
  // out. Once per page that arrived, so a failed load is not retried in a loop.
  const chatListRef = useRef(null);
  const autoLoadedAtRef = useRef(-1);
  const conversationCount = conversations?.length || 0;
  useEffect(() => {
    if (conversationsStatus === 'loading') autoLoadedAtRef.current = -1;
  }, [conversationsStatus]);
  const fillChatList = useCallback(() => {
    const el = chatListRef.current;
    if (!el || searchOpen || !hasMoreConversations || loadingMore || conversationsStatus !== 'ready') return;
    if (autoLoadedAtRef.current === conversationCount) return;
    if (el.scrollHeight - el.clientHeight < 80) {
      autoLoadedAtRef.current = conversationCount;
      onLoadMore();
    }
  }, [conversationCount, conversationsStatus, hasMoreConversations, loadingMore, onLoadMore, searchOpen]);
  useEffect(() => {
    fillChatList();
  }, [fillChatList]);
  useEffect(() => {
    const el = chatListRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => fillChatList());
    observer.observe(el);
    return () => observer.disconnect();
  }, [fillChatList]);

  const closeChatMenu = useCallback(() => {
    setOpenChatMenuId(null);
    setChatMenuPosition(null);
  }, []);

  // The menu is portalled to <body> so the sidebar's own overflow:auto cannot
  // clip it; that means it has to be positioned manually and torn down on any
  // scroll or resize, since it no longer moves with its row.
  const toggleChatMenu = useCallback((conversationId, event) => {
    event.preventDefault();
    event.stopPropagation();
    if (openChatMenuId === conversationId) {
      closeChatMenu();
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    // Rename, Pin and Delete chat (+ the "only the starter" note when disabled).
    const MENU_HEIGHT = hasClientContext ? 92 : 150;
    const MENU_WIDTH = 160; // .unified-chat-menu min-width 150 + border/shadow
    const spaceBelow = window.innerHeight - rect.bottom;
    setChatMenuPosition({
      top: spaceBelow < MENU_HEIGHT ? rect.top - MENU_HEIGHT - 4 : rect.bottom + 4,
      // Keep it on screen: at phone width the kebab sits ~276px in and the menu ran 51px off the right.
      left: Math.max(8, Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8)),
    });
    setOpenChatMenuId(conversationId);
  }, [closeChatMenu, hasClientContext, openChatMenuId]);

  useEffect(() => {
    if (!openChatMenuId) return undefined;
    const handlePointerDown = (event) => {
      if (chatMenuRef.current?.contains(event.target)) return;
      if (event.target?.closest?.('.unified-chat-menu-btn')) return;
      closeChatMenu();
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('scroll', closeChatMenu, true);
    window.addEventListener('resize', closeChatMenu);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('scroll', closeChatMenu, true);
      window.removeEventListener('resize', closeChatMenu);
    };
  }, [openChatMenuId, closeChatMenu]);

  useEffect(() => {
    if (renamingChatId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingChatId]);

  const startRenameChat = useCallback((conversation) => {
    closeChatMenu();
    setRenamingChatId(conversation.conversation_id);
    setRenameDraft(conversation.title || '');
  }, [closeChatMenu]);

  const cancelRenameChat = useCallback(() => {
    setRenamingChatId(null);
    setRenameDraft('');
  }, []);

  const commitRenameChat = useCallback(async (conversation) => {
    const nextTitle = renameDraft.trim();
    // An empty name or an unchanged one is a no-op, not an error — just exit
    // the editor rather than writing a blank title the server would reject.
    if (!nextTitle || nextTitle === (conversation.title || '')) {
      cancelRenameChat();
      return;
    }
    setSavingChatId(conversation.conversation_id);
    try {
      await onUpdateConversation?.(conversation.conversation_id, { title: nextTitle });
      cancelRenameChat();
    } catch (error) {
      // Keep the editor open with the user's text so the attempt isn't lost.
      console.error('Rename failed:', error);
    } finally {
      setSavingChatId(null);
    }
  }, [cancelRenameChat, onUpdateConversation, renameDraft]);

  const togglePinChat = useCallback(async (conversation) => {
    closeChatMenu();
    setSavingChatId(conversation.conversation_id);
    try {
      await onUpdateConversation?.(conversation.conversation_id, { pinned: !conversation.pinned });
    } catch (error) {
      console.error('Pin toggle failed:', error);
    } finally {
      setSavingChatId(null);
    }
  }, [closeChatMenu, onUpdateConversation]);

  const openDeleteChat = useCallback((conversation) => {
    closeChatMenu();
    setDeleteTarget(conversation);
  }, [closeChatMenu]);

  // Gone from the server (or already gone): out of every list here, then
  // MainLayout drops it from its own list and leaves it if it was open.
  const handleChatDeleted = useCallback((cid, info) => {
    setDeleteTarget(null);
    setSearchResults((prev) => (Array.isArray(prev) ? prev.filter((r) => r.conversation_id !== cid) : prev));
    if (renamingChatId === cid) cancelRenameChat();
    onConversationDeleted?.(cid, info);
  }, [cancelRenameChat, onConversationDeleted, renamingChatId]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuOpen && userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  useEffect(() => {
    if (workspaceScope !== 'company') {
      setCompanyScopeConfirmOpen(false);
    }
  }, [workspaceScope]);

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const data = await searchConversations({
          query: searchQuery.trim(),
          ownerUserId: searchOwnerUserId,
          workspaceContext,
        });
        setSearchResults(data.results || []);
      } catch (e) {
        console.error('Search failed:', e);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 1000);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchOwnerUserId, searchQuery, workspaceContext]);

  const toggleSearch = useCallback(() => {
    if (searchOpen) {
      setSearchOpen(false);
      setSearchQuery('');
      setSearchResults(null);
    } else {
      setSearchOpen(true);
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [searchOpen]);

  const handleSearchResultClick = useCallback((result) => {
    loadConversation({ conversation_id: result.conversation_id });
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults(null);
    if (isMobile && onMobileClose) onMobileClose();
  }, [loadConversation, isMobile, onMobileClose]);

  // Define tools/sections
  const getTools = () => {
    return [
      {
        id: 'research',
        name: 'Live Web Research',
        icon: <MdTravelExplore size={18} />,
        description: 'Watch Atlas browse the web'
      },
      {
        id: 'land',
        name: 'Land Evaluation',
        icon: <MdOutlineTerrain size={18} />,
        description: 'Evaluate any address or coordinate: property, deeds, schools, demographics, market, economy, jobs'
      }
    ];
  };

  const tools = getTools();
  const companyOptions = Array.isArray(workspaceCompanyOptions) ? workspaceCompanyOptions : [];
  const selectedCompanyOption = companyOptions.find(
    (company) => String(company?.company_id) === String(selectedWorkspaceCompanyId)
  ) || null;
  const companyLabel = selectedCompanyOption?.company_display_name
    || selectedCompanyOption?.display_name
    || selectedCompanyOption?.company_name
    || workspaceContext?.companyName
    || 'Company';
  const activeWorkspaceLabel = workspaceScope === 'company' ? companyLabel : 'Personal';
  const scopeToggleDisabled = !hasCompanyWorkspace || companyOptions.length === 0;
  const companyScopeInitials = buildCompanyInitials(companyLabel);
  const fileBrowserLabel = workspaceContext?.isCompanyScope ? 'Company File Browser' : 'File Browser';
  const [hoveredWorkspaceScope, setHoveredWorkspaceScope] = useState(null);
  const workspaceHoverLabel = hoveredWorkspaceScope === 'company'
    ? `${companyLabel} Workspace`
    : hoveredWorkspaceScope === 'personal'
      ? 'Personal Workspace'
      : '';

  const selectWorkspaceScope = (nextScope) => {
    if (nextScope === 'company' && scopeToggleDisabled) return;
    if (nextScope === 'company') {
      if (workspaceScope !== 'company') {
        setCompanyScopeDefaultChecked(Boolean(atlasUserPreferences?.start_atlas_in_company_mode));
        setCompanyScopeConfirmOpen(true);
        return;
      }
      setCompanyScopeConfirmOpen(false);
      return;
    } else {
      setCompanyScopeConfirmOpen(false);
    }
    onWorkspaceScopeChange && onWorkspaceScopeChange(nextScope);
  };

  const confirmCompanyScope = async () => {
    setCompanyScopeConfirmOpen(false);
    if (companyScopeDefaultChecked) {
      try {
        setCompanyScopeDefaultSaving(true);
        await onUpdateAtlasUserPreferences?.({ start_atlas_in_company_mode: true });
      } catch (error) {
        console.warn('Failed to save Atlas workspace preference:', error);
      } finally {
        setCompanyScopeDefaultSaving(false);
      }
    }
    if (scopeToggleDisabled) return;
    if (workspaceScope !== 'company') {
      const companyId = selectedWorkspaceCompanyId || companyOptions[0]?.company_id;
      if (companyId && onWorkspaceCompanyChange) {
        onWorkspaceCompanyChange(companyId);
        return;
      }
      onWorkspaceScopeChange && onWorkspaceScopeChange('company');
    }
  };

  const handleToolClick = (toolId) => {
    onToolChange(toolId);
  };

  const deleteChatDialog = deleteTarget ? (
    <DeleteChatDialog
      conversation={deleteTarget}
      onCancel={() => setDeleteTarget(null)}
      onDeleted={handleChatDeleted}
    />
  ) : null;

  // On mobile, sidebar is hidden unless mobileOpen is true
  // Return null on mobile when closed (the sidebar shows as an overlay)
  if (isMobile && !mobileOpen) {
    return null;
  }

  // Collapsed sidebar view (desktop only)
  if (!isMobile && collapsed) {
    return (
      <div className={`unified-sidebar-collapsed ${workspaceScope === 'company' ? 'unified-sidebar-company-mode' : ''}`}>
        {/* Expand button at the top */}
        <button
          type="button"
          className="unified-sidebar-collapsed-btn unified-sidebar-collapsed-expand-btn"
          onClick={(e) => { e.stopPropagation(); onCollapse(false); }}
          title="Expand sidebar"
          aria-label="Expand sidebar"
        >
          <IoChevronForward size={18} />
        </button>
        <button
          type="button"
          className="unified-sidebar-collapsed-btn unified-sidebar-collapsed-new"
          onClick={(e) => { e.stopPropagation(); startNewConversation(); }}
          title="New chat"
          aria-label="New chat"
        >
          <MdAdd size={20} />
        </button>
        <button
          type="button"
          className="unified-sidebar-collapsed-btn"
          onClick={(e) => { e.stopPropagation(); onCollapse(false); }}
          title="Show sidebar"
          aria-label="Show sidebar"
        >
          <MdChat size={20} />
        </button>
        {/* User menu at the bottom */}
        <div className="unified-sidebar-collapsed-user" ref={userMenuRef}>
          <button
            type="button"
            className="unified-sidebar-collapsed-btn unified-sidebar-collapsed-user-btn"
            onClick={(e) => { e.stopPropagation(); setUserMenuOpen(!userMenuOpen); }}
            title={user?.name || user?.email || 'User menu'}
            aria-label="User menu"
          >
            <MdPerson size={18} />
          </button>
          {userMenuOpen && (
            <div className="unified-sidebar-collapsed-dropdown">
              <div className="unified-collapsed-dropdown-header">
                <span className="unified-collapsed-dropdown-name">{user?.name || 'User'}</span>
                <span className="unified-collapsed-dropdown-email">{user?.email || ''}</span>
              </div>
              <button
                type="button"
                className="unified-collapsed-dropdown-item"
                onClick={(e) => {
                  e.stopPropagation();
                  setUserMenuOpen(false);
                  onOpenUserSettings && onOpenUserSettings();
                }}
              >
                <MdSettings size={16} />
                <span>Settings</span>
              </button>
              <button
                type="button"
                className="unified-collapsed-dropdown-item"
                onClick={(e) => {
                  e.stopPropagation();
                  setUserMenuOpen(false);
                  onOpenReleaseNotes && onOpenReleaseNotes();
                }}
              >
                <MdErrorOutline size={16} />
                <span>Release Notes</span>
              </button>
              <button
                type="button"
                className="unified-collapsed-dropdown-item"
                onClick={(e) => {
                  e.stopPropagation();
                  setUserMenuOpen(false);
                  onLogout && onLogout();
                }}
              >
                <MdLogout size={16} />
                <span>Log out</span>
              </button>
            </div>
          )}
        </div>
        {deleteChatDialog}
      </div>
    );
  }

  return (
    <div className={`unified-sidebar ${isMobile ? 'unified-sidebar-mobile' : ''} ${workspaceScope === 'company' ? 'unified-sidebar-company-mode' : ''}`}>
      {/* Logo Header */}
      <div className="unified-sidebar-header">
        <div className="unified-sidebar-header-main">
          <div className="unified-sidebar-brand">
            <img
              className="unified-sidebar-brand-logo"
              src="/realtorch-brand-name-logo.svg"
              alt="RealTorch"
            />
          </div>
          <div
            className="unified-workspace-switcher-wrap"
            onMouseLeave={() => setHoveredWorkspaceScope(null)}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setHoveredWorkspaceScope(null);
              }
            }}
          >
            <div
              className={`unified-workspace-switcher ${workspaceScope === 'company' ? 'company' : 'personal'}`}
              role="group"
              aria-label={`Project scope: ${activeWorkspaceLabel}`}
            >
              <button
                type="button"
                className={`unified-workspace-segment personal ${workspaceScope !== 'company' ? 'active' : ''}`}
                onClick={() => selectWorkspaceScope('personal')}
                onMouseEnter={() => setHoveredWorkspaceScope('personal')}
                onFocus={() => setHoveredWorkspaceScope('personal')}
                aria-label="Personal Workspace"
                aria-pressed={workspaceScope !== 'company'}
              >
                P
              </button>
              <span className="unified-workspace-slider-thumb" aria-hidden="true" />
              <button
                type="button"
                className={`unified-workspace-segment company ${workspaceScope === 'company' ? 'active' : ''}`}
                onClick={() => selectWorkspaceScope('company')}
                onMouseEnter={() => !scopeToggleDisabled && setHoveredWorkspaceScope('company')}
                onFocus={() => !scopeToggleDisabled && setHoveredWorkspaceScope('company')}
                disabled={scopeToggleDisabled}
                aria-label={`${companyLabel} Workspace`}
                aria-pressed={workspaceScope === 'company'}
              >
                {companyScopeInitials}
              </button>
            </div>
            {workspaceHoverLabel && !companyScopeConfirmOpen && (
              <div className="unified-workspace-hover-label" role="status">
                {workspaceHoverLabel}
              </div>
            )}
            {companyScopeConfirmOpen && (
              <div className="unified-workspace-notice" role="dialog" aria-label="Confirm company workspace">
                <div className="unified-workspace-notice-text">
                  Conversations and files submitted here will be accessible by your company.
                </div>
                <label className="unified-workspace-notice-check">
                  <input
                    type="checkbox"
                    checked={companyScopeDefaultChecked}
                    onChange={(e) => setCompanyScopeDefaultChecked(e.target.checked)}
                    disabled={companyScopeDefaultSaving || !atlasUserPreferences?.persistence_available}
                  />
                  <span>Start Atlas in company mode</span>
                </label>
                {companyScopeDefaultChecked && (
                  <div className="unified-workspace-notice-helper">
                    This can be changed in user settings.
                  </div>
                )}
                <div className="unified-workspace-notice-actions">
                  <button type="button" onClick={() => setCompanyScopeConfirmOpen(false)}>Cancel</button>
                  <button type="button" className="primary" onClick={confirmCompanyScope} disabled={companyScopeDefaultSaving}>
                    {companyScopeDefaultSaving ? 'Saving...' : 'OK'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        {isMobile ? (
          <button
            type="button"
            className="unified-sidebar-close-btn"
            onClick={() => onMobileClose && onMobileClose()}
            title="Close menu"
            aria-label="Close menu"
          >
            <IoChevronBack size={20} />
          </button>
        ) : (
          <button
            type="button"
            className="unified-sidebar-collapse-btn"
            onClick={() => onCollapse(true)}
            title="Collapse sidebar"
            aria-label="Collapse sidebar"
          >
            <IoChevronBack size={16} />
          </button>
        )}
      </div>

      {/* Action Buttons */}
      <div className="unified-sidebar-actions">
        <button
          className="unified-sidebar-action-btn"
          type="button"
          onClick={startNewConversation}
          title="New chat"
        >
          <MdAdd size={16} />
          <span>New Chat</span>
        </button>
        <button
          className={`unified-sidebar-action-btn ${searchOpen ? 'active' : ''}`}
          type="button"
          onClick={toggleSearch}
          title="Search chats"
        >
          <MdSearch size={16} />
          <span>Search Chats</span>
        </button>
        <button
          className="unified-sidebar-action-btn"
          type="button"
          onClick={() => onOpenFileBrowser && onOpenFileBrowser()}
          title={fileBrowserLabel}
        >
          <MdFolder size={16} />
          <span>{fileBrowserLabel}</span>
        </button>
      </div>

      {/* Inline Search Input */}
      {searchOpen && (
        <div className="unified-sidebar-search">
          <input
            ref={searchInputRef}
            type="text"
            className="unified-sidebar-search-input"
            placeholder="Search across all chats..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="unified-sidebar-search-clear"
              onClick={() => { setSearchQuery(''); setSearchResults(null); }}
            >
              <IoCloseOutline size={14} />
            </button>
          )}
        </div>
      )}

      {/* Chat History label */}
      <div className="unified-sidebar-section-label">
        Chat History
      </div>

      {/* Chat History / Search Results Section */}
      <div className="unified-sidebar-chats">
        <div
          ref={chatListRef}
          className="unified-sidebar-chat-list"
          onScroll={(e) => {
            if (!hasMoreConversations || loadingMore || searchOpen) return;
            const el = e.currentTarget;
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
              onLoadMore();
            }
          }}
        >
          {searchOpen && searchResults !== null ? (
            <>
              {searchLoading && (
                <div className="unified-sidebar-hint">Searching...</div>
              )}
              {!searchLoading && searchResults.length === 0 && searchQuery.trim() && (
                <div className="unified-sidebar-hint">No results found</div>
              )}
              {searchResults.map((result) => (
                <button
                  key={result.conversation_id}
                  type="button"
                  className="unified-chat-item unified-search-result"
                  onClick={() => handleSearchResultClick(result)}
                >
                  <div className="unified-chat-title">{result.conversation_title}</div>
                  {result.matches?.slice(0, 2).map((match, i) => (
                    <div key={i} className="unified-search-snippet">
                      {renderSnippet(match.snippet)}
                    </div>
                  ))}
                </button>
              ))}
            </>
          ) : (
            <>
              {searchOpen && searchQuery.trim() && !searchResults && (
                <div className="unified-sidebar-hint">Type to search...</div>
              )}
              {!searchOpen && conversationsStatus === 'loading' && (
                <div className="unified-sidebar-loading-state">
                  <span className="unified-sidebar-spinner" />
                  <span>Loading {workspaceContext?.isCompanyScope ? 'company' : 'personal'} chats...</span>
                </div>
              )}
              {!searchOpen && conversationsStatus === 'disabled' && (
                <div className="unified-sidebar-hint">History disabled</div>
              )}
              {!searchOpen && conversationsStatus === 'error' && (
                <div className="unified-sidebar-hint">Failed to load</div>
              )}
              {!searchOpen && conversationsStatus === 'ready' && !conversations.length && (
                <div className="unified-sidebar-hint">No chats yet</div>
              )}
              {!searchOpen && conversationsStatus !== 'loading' && conversations.map((c) => {
                const isActive = c?.conversation_id && c.conversation_id === conversationId;
                const title = c?.title || 'Untitled chat';
                const ts = c?.last_message_at || c?.updated_at;
                const fileCount = c?.file_count || 0;
                const hasBrief = !!c?.active_brief_id;
                const isPinned = !!c?.pinned;
                const isRenaming = renamingChatId === c.conversation_id;
                const isSaving = savingChatId === c.conversation_id;
                const deleteAccess = chatDeleteAccess(c, { user, hasClientContext });

                // The row is a wrapper rather than a single <button> because the
                // kebab is itself a button, and nesting buttons is invalid HTML
                // — the inner one stops receiving clicks in some browsers.
                return (
                  <div
                    key={c.conversation_id}
                    className={`unified-chat-row ${isActive ? 'active' : ''} ${isPinned ? 'pinned' : ''}`}
                  >
                    {isRenaming ? (
                      <div className="unified-chat-rename">
                        <input
                          ref={renameInputRef}
                          className="unified-chat-rename-input"
                          value={renameDraft}
                          maxLength={255}
                          disabled={isSaving}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              commitRenameChat(c);
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelRenameChat();
                            }
                          }}
                          onBlur={() => commitRenameChat(c)}
                        />
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="unified-chat-item"
                          onClick={() => loadConversation(c)}
                          title={title}
                        >
                          <span className="unified-chat-title">
                            {isPinned && (
                              <BsPinAngleFill size={12} className="unified-chat-pin-icon" aria-label="Pinned" />
                            )}
                            {title}
                          </span>
                          <span className="unified-chat-meta">
                            <span className="unified-chat-time">{formatRelativeTime(ts)}</span>
                            <span className="unified-chat-indicators">
                              {fileCount > 0 && (
                                <span className="unified-chat-indicator" title={`${fileCount} file${fileCount !== 1 ? 's' : ''}`}>
                                  <IoFolderOutline size={15} />
                                  <span>{fileCount}</span>
                                </span>
                              )}
                              {hasBrief && (
                                <span className="unified-chat-indicator" title="Has brief">
                                  <IoDocumentOutline size={15} />
                                </span>
                              )}
                            </span>
                          </span>
                        </button>
                        <button
                          type="button"
                          className={`unified-chat-menu-btn ${openChatMenuId === c.conversation_id ? 'active' : ''}`}
                          onClick={(e) => toggleChatMenu(c.conversation_id, e)}
                          aria-label="Chat options"
                          title="Chat options"
                        >
                          <IoEllipsisHorizontal size={16} />
                        </button>
                      </>
                    )}
                    {openChatMenuId === c.conversation_id && chatMenuPosition && createPortal(
                      <div
                        className="unified-chat-menu"
                        ref={chatMenuRef}
                        style={chatMenuPosition}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="unified-chat-menu-item"
                          onClick={() => startRenameChat(c)}
                        >
                          <IoPencilOutline size={14} />
                          <span>Rename</span>
                        </button>
                        <button
                          type="button"
                          className="unified-chat-menu-item"
                          onClick={() => togglePinChat(c)}
                        >
                          {isPinned ? <BsPinAngleFill size={14} /> : <BsPinAngle size={14} />}
                          <span>{isPinned ? 'Unpin' : 'Pin'}</span>
                        </button>
                        {deleteAccess.visible && (
                          <>
                            <button
                              type="button"
                              className="unified-chat-menu-item unified-chat-menu-item-danger"
                              onClick={() => openDeleteChat(c)}
                              disabled={deleteAccess.disabled}
                              title={deleteAccess.disabled ? NOT_STARTER_MESSAGE : undefined}
                            >
                              <IoTrashOutline size={14} />
                              <span>Delete chat</span>
                            </button>
                            {deleteAccess.disabled && (
                              <div className="unified-chat-menu-note">{NOT_STARTER_MESSAGE}</div>
                            )}
                          </>
                        )}
                      </div>,
                      document.body
                    )}
                  </div>
                );
              })}
              {!searchOpen && loadingMore && (
                <div className="unified-sidebar-hint">Loading more…</div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Tools Section */}
      <div className={`unified-sidebar-tools ${toolsExpanded ? 'expanded' : ''}`}>
        <button
          type="button"
          className="unified-sidebar-tools-header"
          onClick={() => setToolsExpanded(!toolsExpanded)}
        >
          <div className="unified-sidebar-section-title">
            <MdBuild size={16} />
            <span>Research Tools</span>
            {selectedTool && !toolsExpanded && (
              <span className="unified-tools-active-indicator" />
            )}
          </div>
          {toolsExpanded ? <MdExpandMore size={20} /> : <MdExpandLess size={20} />}
        </button>

        {toolsExpanded && (
          <div className="unified-sidebar-tools-list">
            {tools.map((tool) => (
              <button
                key={tool.id}
                type="button"
                className={`unified-tool-item ${selectedTool === tool.id ? 'active' : ''}`}
                onClick={() => handleToolClick(tool.id)}
                title={tool.description}
              >
                <span className="unified-tool-icon">{tool.icon}</span>
                <span className="unified-tool-name">{tool.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* User Menu Section */}
      <div className="unified-sidebar-user">
        {/* Admin Client Selector */}
        {user?.isAdmin && clients && clients.length > 0 && (
          <div className="unified-user-client-selector">
            <MdSupervisorAccount size={16} />
            <AdminClientSelector
              clients={clients}
              currentUserId={user.id}
              selectedClient={selectedClient}
              onClientChange={onClientChange}
              onRefreshClients={onRefreshClients}
              variant="sidebar"
            />
          </div>
        )}

        <div className="unified-user-menu-container" ref={userMenuRef}>
          <button
            type="button"
            className="unified-user-menu-trigger"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
          >
            <div className="unified-user-avatar">
              <MdPerson size={18} />
            </div>
            <div className="unified-user-info">
              <span className="unified-user-name">{user?.name || 'User'}</span>
              <span className="unified-user-email">{user?.email || ''}</span>
            </div>
            <MdExpandMore size={18} className={`unified-user-chevron ${userMenuOpen ? 'open' : ''}`} />
          </button>

          {userMenuOpen && (
            <div className="unified-user-dropdown">
              <button
                type="button"
                className="unified-user-dropdown-item"
                onClick={() => {
                  setUserMenuOpen(false);
                  onOpenUserSettings && onOpenUserSettings();
                }}
              >
                <MdSettings size={18} />
                <span>Settings</span>
              </button>
              <button
                type="button"
                className="unified-user-dropdown-item"
                onClick={() => {
                  setUserMenuOpen(false);
                  onOpenReleaseNotes && onOpenReleaseNotes();
                }}
              >
                <MdErrorOutline size={18} />
                <span>Release Notes</span>
              </button>
              <button
                type="button"
                className="unified-user-dropdown-item"
                onClick={() => {
                  setUserMenuOpen(false);
                  onLogout && onLogout();
                }}
              >
                <MdLogout size={18} />
                <span>Log out</span>
              </button>
            </div>
          )}
        </div>
      </div>
      {deleteChatDialog}
    </div>
  );
};

export default UnifiedSidebar;
